import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type React from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { showConfirm } from "@/components/Confirm"
import { DialogOverlay } from "@/components/DialogOverlay"
import { FadeTransition } from "@/components/FadeTransition"
import LoadingCar from "@/components/LoadingCar"
import StrokeText from "@/components/StrokeText"
import { getEventById } from "@/data/events"
import { endingDialogues } from "@/data/guide"
import { createRegions } from "@/data/regions"
import { i18n } from "@/i18n"
import {
    useEventStore,
    useGalleryStore,
    useGlobalStore,
    useGuideStore,
    useMapStore,
    useTransitionStore,
} from "@/store"
import type { MapNode } from "@/types"
import { px2rem } from "@/utils/adaptFont"
import { collectStageFinish } from "@/utils/event"
import { MapBadge } from "./MapBadge"
import { MapNodeComponent } from "./MapNode"

import styles from "./index.module.scss"

interface Props {
    onOpenMapSelect: () => void
    onOpenGallery: () => void
    progressSlot?: React.ReactNode
}

const MOVE_SPEED = 900
const MIN_MOVE_DURATION = 0.35
const MAX_MOVE_DURATION = 1.2

const applyPlayerAnchor = (
    pos: { x: number; y: number },
    anchor?: { x?: number; y?: number },
) => ({
    x: pos.x + (anchor?.x ?? 0),
    y: pos.y + (anchor?.y ?? 0),
})

const buildPathMetrics = (
    points: Array<{ x: number; y: number }>,
): { times: number[]; duration: number } => {
    if (points.length <= 1) {
        return {
            times: [0],
            duration: MIN_MOVE_DURATION,
        }
    }

    const distances = [0]
    let totalDistance = 0

    for (let i = 1; i < points.length; i++) {
        totalDistance += Math.hypot(
            points[i].x - points[i - 1].x,
            points[i].y - points[i - 1].y,
        )
        distances.push(totalDistance)
    }

    if (totalDistance === 0) {
        return {
            times: points.map((_, index) => index / (points.length - 1)),
            duration: MIN_MOVE_DURATION,
        }
    }

    return {
        times: distances.map((distance) => distance / totalDistance),
        duration: Math.min(
            MAX_MOVE_DURATION,
            Math.max(MIN_MOVE_DURATION, totalDistance / MOVE_SPEED),
        ),
    }
}

export const MapView: React.FC<Props> = ({
    onOpenMapSelect,
    onOpenGallery,
    progressSlot,
}) => {
    const mapStore = useMapStore()
    const eventStore = useEventStore()
    const galleryStore = useGalleryStore()
    const fadeTransition = useTransitionStore((s) => s.fadeTransition)
    const guidePhase = useGuideStore((s) => s.guidePhase)
    const startEpilogue = useGuideStore((s) => s.startEpilogue)
    const shouldReduceMotion = useReducedMotion() ?? false

    const pendingActionRef = useRef<(() => void) | null>(null)
    const directionRef = useRef<number>(1)
    const prevRegionIdRef = useRef<string | null>(null)

    useEffect(() => {
        if (mapStore.regions.length === 0) {
            mapStore.setRegions(createRegions())
        }
        if (!mapStore.currentRegionId) {
            const guideDone = useGuideStore.getState().isGuideDone()
            mapStore.initMap(guideDone ? "sargon" : "babel_rhodes")
        }
    }, [
        mapStore.regions.length,
        mapStore.setRegions,
        mapStore.currentRegionId,
        mapStore.initMap,
    ])

    const region = mapStore.getCurrentRegion()
    const isAtEndNode = useMemo(() => {
        if (!region || !mapStore.currentNodeId) return false
        const node = region.nodes.find((n) => n.id === mapStore.currentNodeId)
        return node?.type === "end"
    }, [region, mapStore.currentNodeId])
    const reachableNodes = mapStore.getReachableNodes()
    const reachableIds = useMemo(
        () => new Set(reachableNodes.map((n) => n.id)),
        [reachableNodes],
    )

    const getNodeState = useCallback(
        (node: MapNode) => {
            if (guidePhase === "prologue") return "locked" as const
            if (node.id === mapStore.currentNodeId) return "current" as const
            if (!mapStore.isMoving && reachableIds.has(node.id))
                return "reachable" as const
            if (mapStore.visitedNodeIds.includes(node.id))
                return "visited" as const
            return "locked" as const
        },
        [
            guidePhase,
            mapStore.currentNodeId,
            mapStore.visitedNodeIds,
            mapStore.isMoving,
            reachableIds,
        ],
    )

    const handleEndNode = useCallback(() => {
        const { gameStartTs } = useGlobalStore.getState()
        const regionId = mapStore.currentRegionId
        if (regionId) {
            collectStageFinish(gameStartTs, regionId)
        }

        const nextRegion = mapStore.unlockNextRegion()
        if (!nextRegion) {
            startEpilogue(endingDialogues)
            return
        }
        showConfirm({
            content: i18n.get("map.confirmNextMap"),
            onOk: () => {
                fadeTransition(() => mapStore.initMap(nextRegion.id), "map")
            },
        })
    }, [
        mapStore.currentRegionId,
        mapStore.initMap,
        mapStore.unlockNextRegion,
        fadeTransition,
        startEpilogue,
    ])

    const handleAnimationComplete = useCallback(() => {
        mapStore.setMoving(false)
        pendingActionRef.current?.()
        pendingActionRef.current = null
    }, [mapStore.setMoving])

    const handleNodeClick = useCallback(
        (nodeId: string) => {
            if (mapStore.isMoving) return
            if (!region) return

            const node = region.nodes.find((n) => n.id === nodeId)
            if (!node) return

            if (node.type === "end") {
                if (mapStore.currentNodeId === nodeId) {
                    handleEndNode()
                } else {
                    mapStore.moveToNode(nodeId)
                    pendingActionRef.current = () => handleEndNode()
                }
                return
            }

            mapStore.moveToNode(nodeId)

            if (node.eventId) {
                const event = getEventById(node.eventId)
                if (event) {
                    pendingActionRef.current = () => {
                        if (event.unlockGalleryId) {
                            galleryStore.unlockItem(event.unlockGalleryId)
                        }
                        eventStore.triggerEvent(event)
                    }
                }
            }
        },
        [
            region,
            handleEndNode,
            mapStore.isMoving,
            mapStore.currentNodeId,
            mapStore.moveToNode,
            galleryStore.unlockItem,
            eventStore.triggerEvent,
        ],
    )

    const pathKeyframes = useMemo(() => {
        if (!region || !mapStore.currentNodeId) return null

        const toNode = region.nodes.find((n) => n.id === mapStore.currentNodeId)
        if (!toNode) return null

        const toPos = applyPlayerAnchor(toNode.position, toNode.playerAnchor)

        if (region.id !== prevRegionIdRef.current) {
            prevRegionIdRef.current = region.id
            directionRef.current =
                (region.startDirection ?? "right") === "left" ? -1 : 1
        }

        const fromId = mapStore.previousNodeId
        if (!fromId || !mapStore.isMoving) {
            return {
                left: [px2rem(toPos.x)],
                top: [px2rem(toPos.y)],
                times: [0],
                duration: 0,
                directionX: directionRef.current,
                immediate: true,
            }
        }

        const fromNode = region.nodes.find((n) => n.id === fromId)
        if (!fromNode) {
            return {
                left: [px2rem(toPos.x)],
                top: [px2rem(toPos.y)],
                times: [0],
                duration: 0,
                directionX: directionRef.current,
                immediate: true,
            }
        }

        const fromPos = applyPlayerAnchor(
            fromNode.position,
            fromNode.playerAnchor,
        )
        const edge = mapStore.getEdgeBetween(fromId, mapStore.currentNodeId)
        const waypoints = edge?.waypoints ?? []
        const isReversed = edge?.from === mapStore.currentNodeId
        const points = [
            fromPos,
            ...(isReversed ? [...waypoints].reverse() : waypoints),
            toPos,
        ]

        const dx = toPos.x - fromPos.x
        if (Math.abs(dx) > 20) {
            directionRef.current = dx > 0 ? 1 : -1
        }

        const { times, duration } = buildPathMetrics(points)

        return {
            left: points.map((p) => px2rem(p.x)),
            top: points.map((p) => px2rem(p.y)),
            times,
            duration,
            directionX: directionRef.current,
            immediate: false,
        }
    }, [
        region,
        mapStore.currentNodeId,
        mapStore.previousNodeId,
        mapStore.isMoving,
        mapStore.getEdgeBetween,
    ])

    if (!region) return null

    return (
        <div className={styles.mapView}>
            <div className={styles.mapBg}>
                <div className={styles.mapContainer}>
                    {region.mapImage && (
                        <img
                            className={styles.mapImage}
                            src={region.mapImage}
                            alt=""
                            draggable={false}
                        />
                    )}

                    {region.nodes.map((node) => {
                        const iconConfig =
                            node.iconOverride ??
                            region.iconConfig[node.iconType]
                        return (
                            <MapNodeComponent
                                key={node.id}
                                node={node}
                                state={getNodeState(node)}
                                iconConfig={iconConfig}
                                onClick={handleNodeClick}
                            />
                        )
                    })}

                    {guidePhase !== "prologue" && pathKeyframes && (
                        <motion.div
                            className={styles.player}
                            style={
                                isAtEndNode
                                    ? {
                                          pointerEvents: "auto",
                                          cursor: "pointer",
                                      }
                                    : undefined
                            }
                            onClick={isAtEndNode ? handleEndNode : undefined}
                            animate={{
                                left: pathKeyframes.left,
                                top: pathKeyframes.top,
                            }}
                            transition={
                                pathKeyframes.immediate
                                    ? { duration: 0 }
                                    : {
                                          duration: pathKeyframes.duration,
                                          ease: "linear",
                                          times: pathKeyframes.times,
                                      }
                            }
                            onAnimationComplete={
                                pathKeyframes.immediate
                                    ? undefined
                                    : handleAnimationComplete
                            }
                        >
                            <motion.div
                                style={{ transformOrigin: "50% 80%" }}
                                animate={{
                                    scaleX:
                                        mapStore.isMoving &&
                                        !pathKeyframes.immediate
                                            ? [1, 1.06, 1]
                                            : 1,
                                    scaleY:
                                        mapStore.isMoving &&
                                        !pathKeyframes.immediate
                                            ? [1, 0.94, 1]
                                            : 1,
                                }}
                                transition={
                                    mapStore.isMoving &&
                                    !pathKeyframes.immediate
                                        ? {
                                              duration: 0.4,
                                              ease: "easeInOut",
                                              repeat: Infinity,
                                              repeatType: "loop",
                                          }
                                        : {
                                              duration: 0.2,
                                          }
                                }
                            >
                                <LoadingCar
                                    direction={pathKeyframes.directionX}
                                    immediate={pathKeyframes.immediate}
                                />
                            </motion.div>
                        </motion.div>
                    )}

                    <AnimatePresence mode="wait">
                        <MapBadge key={region.id} regionId={region.id} />
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        {region.decoImage && (
                            <motion.div
                                key={`deco-${region.id}`}
                                className={
                                    region.decoAnimation === "spin"
                                        ? styles.decoSpin
                                        : styles.decoPatrol
                                }
                                initial={
                                    shouldReduceMotion
                                        ? undefined
                                        : { opacity: 0 }
                                }
                                animate={{ opacity: 1 }}
                                exit={
                                    shouldReduceMotion
                                        ? undefined
                                        : { opacity: 0 }
                                }
                                transition={{ duration: 0.3 }}
                            >
                                <img
                                    className={
                                        region.decoAnimation === "spin"
                                            ? styles.decoSpinSprite
                                            : styles.decoSprite
                                    }
                                    src={region.decoImage}
                                    alt=""
                                    draggable={false}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <DialogOverlay />
                    <FadeTransition scope="map" />
                </div>
            </div>

            <div className={styles.sidebar}>
                {progressSlot}
                <div className={styles.sidebarButtons}>
                    <motion.button
                        className={styles.btnMap}
                        type="button"
                        onClick={onOpenMapSelect}
                        aria-label={i18n.get("map.btnMap")}
                        whileHover={
                            shouldReduceMotion
                                ? undefined
                                : { y: -1, scale: 1.02 }
                        }
                        whileTap={
                            shouldReduceMotion
                                ? undefined
                                : { y: 1, scale: 0.96 }
                        }
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        <StrokeText
                            className={styles.btnText}
                            text={i18n.get("map.btnMap")}
                            fontFamily="source-han-sans-japanese"
                            fontWeight={700}
                            fontSize={34}
                            fillColor="#fff"
                            borderWidth={4}
                            borderColor="#564426"
                            offsetY={0}
                            padding={4}
                            baselineAligned
                        />
                    </motion.button>
                    <motion.button
                        className={styles.btnGallery}
                        type="button"
                        onClick={onOpenGallery}
                        aria-label={i18n.get("map.gallery.title")}
                        whileHover={
                            shouldReduceMotion
                                ? undefined
                                : { y: -1, scale: 1.02 }
                        }
                        whileTap={
                            shouldReduceMotion
                                ? undefined
                                : { y: 1, scale: 0.96 }
                        }
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        <StrokeText
                            className={styles.btnText}
                            text={i18n.get("map.gallery.title")}
                            fontFamily="source-han-sans-japanese"
                            fontWeight={700}
                            fontSize={34}
                            fillColor="#fff"
                            borderWidth={4}
                            borderColor="#564426"
                            offsetY={0}
                            padding={4}
                            baselineAligned
                        />
                    </motion.button>
                </div>
            </div>
        </div>
    )
}
